import { forwardRef } from 'react'
import { tv } from 'tailwind-variants'

import type { ComponentPropsWithoutRef, ForwardedRef } from 'react'
import classNames from 'classnames'

const FlexContainerCss = tv({
  base: 'flex flex-wrap my-0 w-full mx-auto max-w-[1120px] max-lg:max-w-none',
  variants: {
    flexWrap: {
      wrap: 'flex-wrap',
      nowrap: 'flex-nowrap',
    },
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
    },
    withGap: {
      true: {
        gap: 'gap-8 max-lg:gap-7 max-md:gap-5',
      },
    },
  },
  defaultVariants: {
    flexWrap: 'wrap',
    justify: 'start',
    withGap: false,
  },
})

export type FlexContainerProps = ComponentPropsWithoutRef<'div'> & {
  flexWrap?: 'wrap' | 'nowrap'
  justify?: 'start' | 'center' | 'end' | 'between'
  withGap?: boolean
}

//

export const FlexContainer = ({
  children,
  className,
  flexWrap,
  justify,
  withGap,
  style,
  ...props
}: FlexContainerProps) => {
  return (
    <div
      style={style}
      className={classNames(FlexContainerCss({ flexWrap, justify, withGap }), className)}
      {...props}
    >
      {children}
    </div>
  )
}

/** TAILWIND does not work with dynamic values. but there sure be a better way to do this when we have time to think about it */
/** Im tempted to remove this flexitem in favour of other thing */

type FlexItemProps = ComponentPropsWithoutRef<'div'> & {
  desktop?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
  laptop?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
  tablet?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9
  phone?: 1 | 2 | 3 | 4 | 5 | 6 | 7
}

const FlexItemCss = tv({
  base: 'w-full',
  variants: {
    desktop: {
      1: 'w-1/12',
      2: 'w-2/12',
      3: 'w-3/12',
      4: 'w-4/12',
      5: 'w-5/12',
      6: 'w-6/12',
      7: 'w-7/12',
      8: 'w-8/12',
      9: 'w-9/12',
      10: 'w-10/12',
      11: 'w-11/12',
      12: 'w-full',
    },
    laptop: {
      1: 'max-xl:w-1/12',
      2: 'max-xl:w-2/12',
      3: 'max-xl:w-3/12',
      4: 'max-xl:w-4/12',
      5: 'max-xl:w-5/12',
      6: 'max-xl:w-6/12',
      7: 'max-xl:w-7/12',
      8: 'max-xl:w-8/12',
      9: 'max-xl:w-9/12',
      10: 'max-xl:w-10/12',
      11: 'max-xl:w-11/12',
      12: 'max-xl:w-full',
    },
    tablet: {
      1: 'max-lg:w-[11%]',
      2: 'max-lg:w-[22%]',
      3: 'max-lg:w-[33%]',
      4: 'max-lg:w-[44%]',
      5: 'max-lg:w-[55%]',
      6: 'max-lg:w-[66%]',
      7: 'max-lg:w-[77%]',
      8: 'max-lg:w-[88%]',
      9: 'max-lg:w-full',
    },
    phone: {
      1: 'max-md:w-[14%]',
      2: 'max-md:w-[28%]',
      3: 'max-md:w-[42%]',
      4: 'max-md:w-[57%]',
      5: 'max-md:w-[71%]',
      6: 'max-md:w-[85%]',
      7: 'max-md:w-full',
    },
  },
})

export const FlexItem = forwardRef(
  (
    { children, desktop, laptop, tablet, phone, className, ...props }: FlexItemProps,
    ref?: ForwardedRef<HTMLDivElement>,
  ) => {
    return (
      <div
        {...props}
        className={classNames(
          FlexItemCss({ desktop, laptop, tablet: tablet || 9, phone: phone || 7 }),
          className,
        )}
        ref={ref}
      >
        {children}
      </div>
    )
  },
)
FlexItem.displayName = 'FlexItem'
